// The shell — PMO's AppShell structure, CdE's navigation.
// Every class here is design-kit CSS except .header-search* and .profile-menu*
// (app plumbing ported from PMO's main.css). Groups are role-gated: an entry a
// role can't use is absent, never greyed.
import { useState, useEffect, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { useTheme } from '../context/ThemeContext';
import { useAccent } from '../context/AccentContext';
import { useSearch } from '../context/SearchContext';
import { useT } from '../i18n';
import { ProfileMenu } from './ProfileMenu';
import { NotificationBell } from 'notification-kit/client/index.js';
import { api } from '../api/client';
import type { Page } from '../App';

const SEARCH_PAGES: Page[] = ['repository', 'tickets'];

const PAGE_TITLE_KEYS: Record<Page, string> = {
  home: 'shell.title.home', repository: 'shell.title.repository', process: 'shell.title.process',
  governance: 'shell.title.governance', observe: 'shell.title.observe', tickets: 'shell.title.tickets',
  admin: 'shell.title.admin', 'cde-process': 'shell.title.cde-process', profile: 'shell.title.profile',
};

export function AppShell({ children, currentPage, onNavigate }: {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
}) {
  const { user, logout, impersonatedBy, stopImpersonating } = useAuth() as any;
  const { role, isAdmin, canSeeKpis, isGovernance, isCde } = useRole();
  const { theme, toggleTheme } = useTheme();
  const { logoUrl } = useAccent();
  const { searchTerm, setSearchTerm } = useSearch();
  const { t } = useT();

  const showSearch = SEARCH_PAGES.includes(currentPage);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [suiviOpen, setSuiviOpen] = useState(() => localStorage.getItem('suiviOpen') !== 'false');
  const [processusOpen, setProcessusOpen] = useState(() => localStorage.getItem('processusOpen') !== 'false');

  // live counters: the sidebar is a worklist, not a menu
  const [counts, setCounts] = useState<{ governance?: number; tickets?: number; mywork?: number }>({});
  useEffect(() => {
    let active = true;
    const load = () => {
      if (isGovernance || isCde) api.getGovernancePending().then(r => active && setCounts(c => ({ ...c, governance: r.length }))).catch(() => {});
      api.getTickets({ status: 'open' }).then(r => active && setCounts(c => ({ ...c, tickets: r.length }))).catch(() => {});
      if (isCde) api.getMyWork().then(r => active && setCounts(c => ({ ...c, mywork: r.stats.active + r.to_take.length }))).catch(() => {});
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { active = false; clearInterval(iv); };
  }, [isGovernance, isCde]);

  useEffect(() => { localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed)); }, [sidebarCollapsed]);
  useEffect(() => { localStorage.setItem('suiviOpen', String(suiviOpen)); }, [suiviOpen]);
  useEffect(() => { localStorage.setItem('processusOpen', String(processusOpen)); }, [processusOpen]);
  useEffect(() => {
    if (['home', 'governance', 'observe'].includes(currentPage)) setSuiviOpen(true);
    if (['repository', 'tickets', 'process'].includes(currentPage)) setProcessusOpen(true);
    // the search box is shared; don't let a répertoire query silently filter tickets
    if (!SEARCH_PAGES.includes(currentPage) && searchTerm) setSearchTerm('');
  }, [currentPage]);

  const navItem = (page: Page, label: string, badge?: number) => (
    <a
      href={`#/${page}`}
      className={`nav-link ${currentPage === page || (page === 'repository' && currentPage === 'process') ? 'active' : ''}`}
      onClick={(e) => { e.preventDefault(); onNavigate(page); }}
    >
      {label}
      {badge != null && badge > 0 && <span className="nav-count">{badge}</span>}
    </a>
  );

  const showSuivi = isCde || isGovernance || canSeeKpis;

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {logoUrl && <img src={logoUrl} alt="" className="brand-logo" onClick={() => onNavigate('repository')} />}
            <span className="brand-mark" onClick={() => onNavigate('repository')} style={{ cursor: 'pointer' }}>CDE</span>
            <span className="brand-sep" />
            <span className="brand-page">{t(PAGE_TITLE_KEYS[currentPage])}</span>
          </div>
          <div id="header-actions-portal" className="header-actions"></div>
          {showSearch && (
            <div className="header-search">
              <Search size={14} className="header-search-icon" />
              <input
                className="header-search-input"
                type="text"
                placeholder={t('shell.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="header-search-clear" onClick={() => setSearchTerm('')}><X size={12} /></button>
              )}
            </div>
          )}
        </div>

        <div className="topbar-right">
          <NotificationBell onNavigate={(hash: string) => { window.location.hash = hash; }} />
          <button className="theme-btn" onClick={toggleTheme}
                  title={theme === 'light' ? t('common.theme.toDark') : t('common.theme.toLight')}>
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <ProfileMenu onOpenProfile={() => onNavigate('repository')} />
          {isAdmin && (
            <span className="badge badge-accent badge-clickable" onClick={() => onNavigate('admin')} title={t('shell.adminBadge')}>
              {t('shell.adminBadge')}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout}>{t('common.logout')}</button>
        </div>
      </header>

      <div className={`shell ${sidebarCollapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? '›' : t('shell.collapse')}
          </button>

          {!sidebarCollapsed && (
            <nav className="sidebar-nav">
              {showSuivi && (
                <div className="nav-section">
                  <div className="nav-section-title nav-section-title-clickable" onClick={() => setSuiviOpen(!suiviOpen)}>
                    <span>{t('shell.group.suivi')}</span>
                    <span className="nav-dropdown-arrow">{suiviOpen ? '▾' : '▸'}</span>
                  </div>
                  {suiviOpen && (
                    <>
                      {isCde && navItem('home', t('shell.nav.home'), counts.mywork)}
                      {(isGovernance || isCde) && navItem('governance', t('shell.nav.governance'), counts.governance)}
                      {canSeeKpis && navItem('observe', t('shell.nav.observe'))}
                    </>
                  )}
                </div>
              )}

              <div className="nav-section">
                <div className="nav-section-title nav-section-title-clickable" onClick={() => setProcessusOpen(!processusOpen)}>
                  <span>{t('shell.group.processus')}</span>
                  <span className="nav-dropdown-arrow">{processusOpen ? '▾' : '▸'}</span>
                </div>
                {processusOpen && (
                  <>
                    {navItem('repository', t('shell.nav.repository'))}
                    {navItem('tickets', t('shell.nav.tickets'), counts.tickets)}
                  </>
                )}
              </div>

              <div className="nav-section">
                {navItem('cde-process', t('shell.nav.cdeProcess'))}
                {isAdmin && navItem('admin', t('shell.nav.admin'))}
              </div>
            </nav>
          )}
        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>{t('shell.impersonating', { name: user?.name ?? '', by: impersonatedBy.name })}</span>
              <button className="btn btn-sm" onClick={() => { stopImpersonating(); window.location.reload(); }}>
                {t('shell.stopImpersonating')}
              </button>
            </div>
          )}
          <div className="page">{children}</div>
        </main>
      </div>
    </>
  );
}
